import clsx from 'clsx';
import styles from './MatchQuestion.module.scss';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MatchingBox } from '~/components';
import { shuffleArray } from '~/utils/common';

// Hàm tạo mảng với n phần tử
function createArray(n, language) {
    let array = [];
    for (let i = 0; i < n; i++) {
        array.push({
            id: uuidv4(),
            status: 'disable',
            text: '',
            language: language,
            animationRight: false,
        });
    }
    return array;
}

function updateRandomElementText(arr, newText) {
    // Lọc các phần tử có status === 'disable'
    let filteredArray = arr.filter((item) => item.status === 'disable');

    // Nếu không có phần tử nào thỏa mãn điều kiện, trả về mảng gốc
    if (filteredArray.length === 0) return arr;

    // Lấy ngẫu nhiên một phần tử từ mảng đã lọc
    let randomIndex = Math.floor(Math.random() * filteredArray.length);
    let selectedElement = filteredArray[randomIndex];

    // Tìm index của phần tử đã chọn trong mảng gốc
    let originalIndex = arr.indexOf(selectedElement);

    // Cập nhật text của phần tử đã chọn
    arr[originalIndex] = {
        ...arr[originalIndex],
        text: newText,
        status: 'normal',
    };

    return arr;
}

function MatchQuestion(
    {
        question,
        audioMap,
        notiCompleteAnAnswer,
        updateResult,
        className,
        handlePlay,
        handleStop,
        ...rest
    },
    ref,
) {
    const [numPair] = useState(5);
    const [numBlank, setNumBlank] = useState(numPair);
    const [listBoxVi, setListBoxVi] = useState(createArray(numPair, 'vi'));
    const [listBoxEn, setListBoxEn] = useState(createArray(numPair, 'en'));
    const [count, setCount] = useState(0);
    const [choosing, setChoosing] = useState(null);
    const [countCheck, setCountCheck] = useState(0);
    const [countRight, setCountRight] = useState(0);
    const timeoutIdRef = useRef(null);
    const timeoutIdRef2 = useRef(null);
    const [showedAllPair, setShowedAllPair] = useState(false);

    const updateNewPair = useCallback(
        (n) => {
            // console.log('updateNewPair');
            let dem = 0;
            while (true) {
                if (count + dem > question.pairs.length - 1) {
                    // đã hết cặp từ mới
                    console.log('đã hết cặp từ mới');
                    setShowedAllPair(true);
                    break;
                }
                // cập nhật 1 cặp từ
                const pair = question.pairs[count + dem];
                setListBoxVi((prev) => updateRandomElementText(prev, pair.vi));
                setListBoxEn((prev) => updateRandomElementText(prev, pair.en));

                dem++;
                if (dem >= n) {
                    console.log('đã đủ số lượng mong muốn');
                    // đã đủ số lượng mong muốn
                    break;
                }
            }

            setCount((pre) => {
                // console.log('setCount', pre + dem);
                return pre + dem;
            });
            return dem;
        },
        [count, question.pairs],
    );

    useEffect(() => {
        if (countRight === question.pairs.length)
            updateResult({ count: 0, correct: 0 }, true);
    }, [countCheck, countRight, question.pairs.length, updateResult]);

    useEffect(() => {
        question.pairs = shuffleArray(question.pairs);
    }, [question]);

    useEffect(() => {
        // console.log('numBlank', numBlank);
        if (!showedAllPair && numBlank >= 3) {
            if (timeoutIdRef2 && timeoutIdRef2.current)
                clearTimeout(timeoutIdRef2.current);
            timeoutIdRef2.current = setTimeout(
                () => {
                    const dem = updateNewPair(numBlank);
                    setNumBlank((pre) => pre - dem);
                },
                numBlank === numPair ? 0 : 1000,
            );
        }
    }, [numBlank, showedAllPair, numPair, updateNewPair]);

    const handleOnClickBox = (box) => {
        if (question.type === 'matching-listening') {
            if (box.status === 'normal' || box.status === 'error' || box.status === 'choosen')
                if (handlePlay)
                    handlePlay(box.text);
        }
        // console.log('timeoutIdRef2.current', timeoutIdRef2.current);
        if (choosing === null) {
            if (box.status === 'normal' || box.status === 'error') {
                setBoxStatus(box, 'choosen');
                setChoosing({ ...box, status: 'choosen' });
            } else {
                console.log('Box đã ẩn rồi');
            }
        } else {
            if (box.id === choosing.id) {
                setBoxStatus(box, 'normal');
                setChoosing(null);
            } else {
                if (box.language === choosing.language) {
                    setBoxStatus(choosing, 'normal');
                    setBoxStatus(box, 'choosen');
                    setChoosing(box);
                } else {
                    // xử lý check đúng hay sai
                    let kq = !!question.pairs.find(
                        (pair) =>
                            (pair.en === box.text &&
                                pair.vi === choosing.text) ||
                            (pair.vi === box.text && pair.en === choosing.text),
                    );
                    // console.log('kq', kq);
                    setCountCheck((prev) => prev + 1);
                    if (kq) {
                        notiCompleteAnAnswer('right');
                        updateResult({ count: 1, correct: 1 }, false);
                        setBoxStatus(box, 'disable');
                        setBoxStatus(choosing, 'disable');
                        setBoxAnimationRight(box, true);
                        setBoxAnimationRight(choosing, true);
                        setChoosing(null);
                        setNumBlank((prev) => prev + 1);
                        setCountRight((prev) => prev + 1);
                        // Thiết lập timeout để tự động chuyển lại trạng thái sau 1 giây
                        timeoutIdRef.current = setTimeout(() => {
                            setAllBoxFromErrorToNormal();
                        }, 1000);

                        return;
                    } else {
                        notiCompleteAnAnswer('wrong');
                        updateResult({ count: 1, correct: 0 }, false);
                        setBoxStatus(box, 'error');
                        setBoxStatus(choosing, 'error');
                        setChoosing(null);

                        // Thiết lập timeout để tự động chuyển lại trạng thái sau 1 giây
                        timeoutIdRef.current = setTimeout(() => {
                            setAllBoxFromErrorToNormal();
                        }, 1000);

                        return;
                    }
                }
            }
        }
        // tìm đổi hết các box error thành normal
        setAllBoxFromErrorToNormal();
    };

    const setAllBoxFromErrorToNormal = () => {
        // console.log('setAllBoxFromErrorToNormal');
        if (timeoutIdRef && timeoutIdRef.current)
            clearTimeout(timeoutIdRef.current);
        setListBoxVi((prevList) =>
            prevList.map((item) =>
                item.status === 'error'
                    ? { ...item, status: 'normal' }
                    : item.animationRight
                        ? { ...item, animationRight: false }
                        : item,
            ),
        );
        setListBoxEn((prevList) =>
            prevList.map((item) =>
                item.status === 'error'
                    ? { ...item, status: 'normal' }
                    : item.animationRight
                        ? { ...item, animationRight: false }
                        : item,
            ),
        );
    };

    const setBoxStatus = (box, status) => {
        // console.log('box', box);
        box.language === 'vi'
            ? setListBoxVi((prevList) =>
                prevList.map((item) =>
                    item.id === box.id ? { ...item, status } : item,
                ),
            )
            : setListBoxEn((prevList) =>
                prevList.map((item) =>
                    item.id === box.id ? { ...item, status } : item,
                ),
            );
    };

    const setBoxAnimationRight = (box, animationRight) => {
        // console.log('box', box)
        box.language === 'vi'
            ? setListBoxVi((prevList) =>
                prevList.map((item) =>
                    item.id === box.id ? { ...item, animationRight } : item,
                ),
            )
            : setListBoxEn((prevList) =>
                prevList.map((item) =>
                    item.id === box.id ? { ...item, animationRight } : item,
                ),
            );
    };

    useEffect(() => {
        return () => {
            if (handleStop)
                handleStop();
        };
        // eslint-disable-next-line
    }, [])

    return (
        <div {...rest} className={clsx(styles.question, className)}>
            <div className={clsx(styles.column)}>
                {listBoxEn.map((box) => {
                    return (
                        <MatchingBox
                            box={box}
                            key={box.id}
                            onClick={() => handleOnClickBox(box)}
                            audioMap={audioMap}
                            questionType={question.type}
                        ></MatchingBox>
                    );
                })}
            </div>
            <div className={clsx(styles.column)}>
                {listBoxVi.map((box) => {
                    return (
                        <MatchingBox
                            box={box}
                            key={box.id}
                            onClick={() => handleOnClickBox(box)}
                        ></MatchingBox>
                    );
                })}
            </div>
        </div>
    );
}
export default forwardRef(MatchQuestion);
